/**
 * WordPress dependencies
 */
import { useState, useMemo } from '@wordpress/element';
import { useDispatch } from '@wordpress/data';
import { Modal, SearchControl, Notice } from '@wordpress/components';
import { useAsyncList } from '@wordpress/compose';
import {
	BlockContextProvider,
	store as blockEditorStore,
	__experimentalBlockPatternsList as BlockPatternsList, // eslint-disable-line
} from '@wordpress/block-editor';
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import {
	useBlockNameForPatterns,
	getTransformedBlocksFromPattern,
	usePatterns,
	searchPatterns,
} from './utils';

export default function PatternSelectionModal( {
	clientId,
	attributes,
	setIsPatternSelectionModalOpen,
	name,
	hasExistingPattern,
} ) {
	const [ searchValue, setSearchValue ] = useState( '' );
	const { replaceBlock, selectBlock } = useDispatch( blockEditorStore );
	const onBlockPatternSelect = ( _, blocks ) => {
		const { newBlocks, queryClientIds } = getTransformedBlocksFromPattern(
			blocks,
			attributes,
			name
		);
		replaceBlock( clientId, newBlocks );
		if ( queryClientIds[ 0 ] ) {
			selectBlock( queryClientIds[ 0 ] );
		}
	};
	// When we preview Query Loop blocks we should prefer the current
	// block's postType, which is passed through block context.
	const blockPreviewContext = useMemo(
		() => ( {
			previewPostType: 'portal',
		} ),
		[ 'portal' ]
	);
	const blockNameForPatterns = useBlockNameForPatterns(
		clientId,
		attributes,
		name
	);
	const blockPatterns = usePatterns( clientId, blockNameForPatterns );
	const filteredBlockPatterns = useMemo( () => {
		return searchPatterns( blockPatterns, searchValue );
	}, [ blockPatterns, searchValue ] );
	const shownBlockPatterns = useAsyncList( filteredBlockPatterns );

	return (
		<Modal
			overlayClassName="block-library-query-pattern__selection-modal"
			title={ __( 'Choose a Layout', 'suredash' ) }
			onRequestClose={ () => setIsPatternSelectionModalOpen( false ) }
			isFullScreen
		>
			<div className="block-library-query-pattern__selection-content">
				{ hasExistingPattern && (
					<Notice status="warning" isDismissible={ false } className="block-library-query-pattern__selection-notice">
						{ __( 'Changing this layout will reset the logos and block-level colors configured for the current layout.', 'suredash' ) }
					</Notice>
				) }
				<div className="block-library-query-pattern__selection-search">
					<SearchControl
						__nextHasNoMarginBottom
						onChange={ setSearchValue }
						value={ searchValue }
						label={ __( 'Search for patterns', 'suredash' ) }
						placeholder={ __( 'Search', 'suredash' ) }
					/>
				</div>
				<BlockContextProvider value={ blockPreviewContext }>
					<BlockPatternsList
						blockPatterns={ filteredBlockPatterns }
						shownPatterns={ shownBlockPatterns }
						onClickPattern={ onBlockPatternSelect }
					/>
				</BlockContextProvider>
			</div>
		</Modal>
	);
}
